import { Injectable, inject } from '@angular/core';
import { AuthApiService, CatalogueApiService } from '@angular-workspace/services';
import type { OgcOnboardingMode, ResourceServerId } from '../dataset-home/dataset-home.models';
import {
  buildResourceServerPayloadArray,
  CATALOGUE_APD_URL,
  normalizeResourceServerId,
  RESOURCE_SERVER_URL_CONFIG,
  resourceServerIdFromCreateItemResponse,
  resourceServerKeysFromPayload,
  unwrapCatalogueApiResult,
  type ResourceServerUrlConfig,
} from '../resource-server.util';
import { buildDataDescriptorPayload } from './data-descriptor.logic';
import type { NgsildOnboardingPayload, NgsildYearRange } from './ngsild.models';
import type { ModelOnboardingPayload } from '../../model/model.models';
import {
  resolveVerifiedUploadRequestType,
  type NgsildUploadMethod,
  type VerifiedUploadRequestType,
} from './upload-request-type.util';

/** Result of POST catalogue `item` for NGSI-LD dataset onboarding. */
export interface NgsildCreateItemResult {
  itemId: string;
  payload: NgsildOnboardingPayload;
  resourceServerId: ResourceServerId;
  resourceServerKeys: string[];
}

/** Result of POST catalogue `item` for model onboarding. */
export interface ModelCreateItemResult {
  itemId: string;
  payload: ModelOnboardingPayload;
}

@Injectable({ providedIn: 'root' })
export class NgsildItemService {
  private readonly catalogueApi = inject(CatalogueApiService);
  private readonly authApi = inject(AuthApiService);
  private readonly resourceServerUrls =
    inject(RESOURCE_SERVER_URL_CONFIG, { optional: true }) ?? ({} as ResourceServerUrlConfig);
  private readonly catalogueApdUrl = inject(CATALOGUE_APD_URL, { optional: true }) ?? '';

  /**
   * Update an existing dataset with a repository / data source URL (same PUT `item` API as modify asset).
   * Preserves `itemStatus` and `publishStatus` from the current item API response.
   */
  async updateDatasetWithDataSourceUrl(
    itemId: string,
    mediaUrl: string,
    onboarding: NgsildOnboardingPayload,
    resourceServer: ResourceServerId = 'ngsi-ld',
  ): Promise<void> {
    const trimmedUrl = mediaUrl.trim();
    if (!itemId?.trim()) {
      throw new Error('Item ID is required to update the dataset');
    }
    if (!trimmedUrl) {
      throw new Error('Data source URL is required');
    }

    const id = itemId.trim();
    const createPayload = await this.buildCreateItemPayload(onboarding, resourceServer);
    const statusFields = await this.resolveItemStatusFieldsFromItemApi(id);
    await this.catalogueApi.updateItem({
      ...createPayload,
      id,
      mediaURL: trimmedUrl,
      ...statusFields,
    });
  }

  /**
   * Set catalogue `itemStatus` after SFTP / API / Direct Push data-upload configuration (PUT `item` API).
   * Also includes `itemStatus` / `publishStatus` from the current item API response, then applies the
   * upload-method `itemStatus` (`VERIFIED-SFTP` | `UNVERIFIED-SFTP` | `VERIFIED-API` |
   * `UNVERIFIED-API` | `Direct`).
   *
   * When **`onboarding` is provided** — matches legacy dataset onboarding (`contribution-steps/upload`):
   * spreads `buildCreateItemPayload` plus status fields from GET item.
   *
   * When **`onboarding` is missing** — sends `{ id, ...statusFields, itemStatus }`.
   */
  async updateDatasetItemStatus(
    itemId: string,
    uploadMethod: NgsildUploadMethod | 'Direct',
    onboarding?: NgsildOnboardingPayload | null,
    resourceServer?: ResourceServerId,
  ): Promise<void> {
    const id = itemId.trim();
    if (!id) {
      throw new Error('Item ID is required to update the dataset');
    }

    const statusFields = await this.resolveItemStatusFieldsFromItemApi(id);
    const itemStatus: VerifiedUploadRequestType | 'Direct' =
      uploadMethod === 'Direct'
        ? 'Direct'
        : resolveVerifiedUploadRequestType(statusFields.itemStatus, uploadMethod);

    if (onboarding) {
      const rs = normalizeResourceServerId(resourceServer ?? 'ngsi-ld');
      const createPayload = await this.buildCreateItemPayload(onboarding, rs);
      await this.catalogueApi.updateItem({
        ...createPayload,
        id,
        ...statusFields,
        itemStatus,
      });
      return;
    }

    await this.catalogueApi.updateItem({
      id,
      ...statusFields,
      itemStatus,
    });
  }

  /**
   * Resolve `VERIFIED-*` / `UNVERIFIED-*` upload request type from the asset's current itemStatus.
   */
  async resolveUploadRequestType(
    itemId: string,
    method: NgsildUploadMethod,
  ): Promise<VerifiedUploadRequestType> {
    const id = itemId.trim();
    if (!id) {
      return resolveVerifiedUploadRequestType(undefined, method);
    }
    const statusFields = await this.resolveItemStatusFieldsFromItemApi(id);
    return resolveVerifiedUploadRequestType(statusFields.itemStatus, method);
  }

  async updateModelWithDataSourceUrl(
    itemId: string,
    mediaUrl: string,
    onboarding: ModelOnboardingPayload,
  ): Promise<void> {
    const trimmedUrl = mediaUrl.trim();
    if (!itemId?.trim()) {
      throw new Error('Item ID is required to update the model');
    }
    if (!trimmedUrl) {
      throw new Error('Data source URL is required');
    }

    const id = itemId.trim();
    const createPayload = await this.buildModelCreateItemPayload(onboarding);
    const statusFields = await this.resolveItemStatusFieldsFromItemApi(id);
    await this.catalogueApi.updateItem({
      ...createPayload,
      id,
      mediaURL: trimmedUrl,
      ...statusFields,
    });
  }

  /**
   * Read `itemStatus` and `publishStatus` (or `publishAssets`) from GET item so PUT does not drop them.
   */
  private async resolveItemStatusFieldsFromItemApi(
    itemId: string,
  ): Promise<{ itemStatus?: string; publishStatus?: string }> {
    try {
      const response = await this.catalogueApi.getItemById(itemId);
      const record = this.unwrapItemRecord(response);
      if (!record) {
        return {};
      }

      const itemStatus = record['itemStatus'] ?? record['item_status'];
      const publishStatus =
        record['publishStatus'] ??
        record['publish_status'] ??
        record['publishAssets'] ??
        record['publish_assets'];

      const fields: { itemStatus?: string; publishStatus?: string } = {};
      if (itemStatus != null && String(itemStatus).trim() !== '') {
        fields.itemStatus = String(itemStatus).trim();
      }
      if (publishStatus != null && String(publishStatus).trim() !== '') {
        fields.publishStatus = String(publishStatus).trim();
      }
      return fields;
    } catch (error) {
      console.warn('Could not load item status fields for PUT payload:', error);
      return {};
    }
  }

  private unwrapItemRecord(response: unknown): Record<string, unknown> | null {
    if (!response || typeof response !== 'object') {
      return null;
    }
    const root = response as Record<string, unknown>;
    const result = root['result'];
    if (Array.isArray(result) && result.length > 0 && result[0] && typeof result[0] === 'object') {
      return result[0] as Record<string, unknown>;
    }
    const unwrapped = unwrapCatalogueApiResult(response);
    if (!unwrapped) {
      return null;
    }
    if (Array.isArray(unwrapped) && unwrapped.length > 0 && unwrapped[0] && typeof unwrapped[0] === 'object') {
      return unwrapped[0] as Record<string, unknown>;
    }
    return unwrapped;
  }

  async createModelItem(onboarding: ModelOnboardingPayload): Promise<ModelCreateItemResult> {
    const apiPayload = await this.buildModelCreateItemPayload(onboarding);
    const response = await this.catalogueApi.createAssetItem(apiPayload, {
      isToasterRequired: false,
    });
    const itemId = this.extractItemId(response);
    return { itemId, payload: onboarding };
  }

  async createDatasetItem(
    onboarding: NgsildOnboardingPayload,
    resourceServer: ResourceServerId = 'ngsi-ld',
    ogcOnboardingMode?: OgcOnboardingMode | null,
  ): Promise<NgsildCreateItemResult> {
    const apiPayload = await this.buildCreateItemPayload(onboarding, resourceServer, ogcOnboardingMode);
    const response = await this.catalogueApi.createAssetItem(apiPayload, {
      isToasterRequired: false,
    });
    const itemId = this.extractItemId(response);
    const apiResult = unwrapCatalogueApiResult(response);
    const resourceServerKeys = resourceServerKeysFromPayload(apiResult);
    const fromApi = resourceServerIdFromCreateItemResponse(response);
    const resourceServerId = fromApi ?? normalizeResourceServerId(resourceServer);
    return {
      itemId,
      payload: onboarding,
      resourceServerId,
      resourceServerKeys,
    };
  }

  /** Same resolution order as contribution-steps create/update flows. */
  private extractItemId(response: Record<string, unknown>): string {
    const result = response['result'] as Record<string, unknown> | undefined;
    const data = response['data'] as Record<string, unknown> | undefined;
    const resultData = result?.['data'] as Record<string, unknown> | undefined;

    const id =
      result?.['itemId'] ??
      result?.['id'] ??
      data?.['id'] ??
      response['id'] ??
      resultData?.['id'] ??
      resultData?.['itemId'];

    if (id == null || id === '') {
      throw new Error('Item ID not found in create item API response');
    }
    return String(id);
  }

  private resolveLicense(license: string, otherLicense: string): string {
    return license === 'Other'
      ? otherLicense?.trim() || 'Other'
      : license;
  }

  private mapAccessPolicy(permission: string | undefined | null): string {
    const normalized = (permission ?? '').trim();
    if (
      normalized === 'Restricted' ||
      normalized.toUpperCase() === 'RESTRICTED'
    ) {
      return 'RESTRICTED';
    }
    if (
      normalized === 'Private' ||
      normalized === 'Privacy Computation' ||
      normalized.toUpperCase() === 'PRIVATE'
    ) {
      return 'PRIVATE';
    }
    return 'OPEN';
  }

  private async buildModelCreateItemPayload(
    onboarding: ModelOnboardingPayload,
  ): Promise<Record<string, unknown>> {
    const org = await this.resolveOrganizationContext();
    const { basic, metadata } = onboarding;
    const accessPolicy = this.mapAccessPolicy(basic.permission);
    const license = this.resolveLicense(metadata.license, metadata.otherLicense);

    return {
      type: ['adex:AiModel'],
      name: basic.title?.trim() ?? '',
      description: basic.longDescription?.trim() ?? '',
      shortDescription: basic.shortDescription?.trim() ?? '',
      label: basic.title?.trim() ?? '',
      tags: metadata.tags ?? [],
      accessPolicy,
      apdURL: this.catalogueApdUrl,
      organizationId: org.organizationId,
      organizationType: org.organizationType,
      department: org.department,
      modelType: metadata.modelType?.trim() ?? '',
      fileFormat: (metadata.fileFormat || '').toString().toLowerCase(),
      industry: metadata.industry?.trim() || org.industry || 'forest',
      uploadedBy: org.uploadedBy,
      license: license || 'MIT',
      fileSize: '15MB',
      resourceServer: this.buildResourceServerArray('file-server'),
    };
  }

  private async resolveOrganizationContext(): Promise<{
    organizationId: string;
    organizationType: string;
    industry: string;
    department: string;
    uploadedBy: string;
    ownerUserId: string;
  }> {
    let userData: Record<string, unknown> | null = null;
    let organizationsList: Array<Record<string, unknown>> = [];

    try {
      const rawUser = await this.authApi.getUserInfo();
      userData =
        (rawUser as { result?: Record<string, unknown> })?.result ??
        (rawUser as Record<string, unknown>);
      const orgsResponse = await this.authApi.getAllOrganisations();
      const orgs = orgsResponse as { result?: unknown[]; data?: unknown[] };
      organizationsList = (orgs?.result ?? orgs?.data ?? []) as Array<
        Record<string, unknown>
      >;
    } catch (error) {
      console.error('Error fetching user/organization info for model create:', error);
    }

    const result = userData ?? {};
    const organisation = (result['organisation'] as Record<string, unknown>) ?? {};
    const userOrganisationId =
      (organisation['organisationId'] as string) ||
      (organisation['id'] as string) ||
      '';

    let organisationType = 'Public';
    let industry = 'forest';
    let department = '';

    if (userOrganisationId && organizationsList.length > 0) {
      const matchedOrg = organizationsList.find(
        (org) =>
          org['id'] === userOrganisationId ||
          org['organisationId'] === userOrganisationId,
      );
      if (matchedOrg) {
        organisationType =
          (matchedOrg['organisationType'] as string) ||
          (matchedOrg['type'] as string) ||
          'Public';
        industry = (matchedOrg['industry'] as string) || 'forest';
        department =
          (matchedOrg['department'] as string) ||
          (matchedOrg['dept'] as string) ||
          (matchedOrg['departmentName'] as string) ||
          '';
      }
    }

    const uploadedBy =
      (result['email'] as string) ||
      (result['emailAddress'] as string) ||
      (result['registeredEmail'] as string) ||
      (result['userName'] as string) ||
      (result['name'] as string) ||
      'Rajasthan Forest Department Data Provider';

    return {
      organizationId: userOrganisationId || '6e5bd047-9eb8-4bba-b72e-f57ce3e23ce6',
      organizationType: organisationType || 'Public',
      industry: industry || 'forest',
      department,
      uploadedBy,
      ownerUserId:
        (result['userId'] as string) || (result['id'] as string) || '',
    };
  }

  private async buildCreateItemPayload(
    onboarding: NgsildOnboardingPayload,
    resourceServer: ResourceServerId,
    ogcOnboardingMode?: OgcOnboardingMode | null,
  ): Promise<Record<string, unknown>> {
    const { basic, metadata } = onboarding;

    let userData: Record<string, unknown> | null = null;
    let organizationsList: Array<Record<string, unknown>> = [];

    try {
      const rawUser = await this.authApi.getUserInfo();
      userData = (rawUser as { result?: Record<string, unknown> })?.result ?? (rawUser as Record<string, unknown>);
      const orgsResponse = await this.authApi.getAllOrganisations();
      const orgs = orgsResponse as { result?: unknown[]; data?: unknown[] };
      organizationsList = (orgs?.result ?? orgs?.data ?? []) as Array<Record<string, unknown>>;
    } catch (error) {
      console.error('Error fetching user/organization info for item create:', error);
    }

    const result = userData ?? {};
    const organisation = (result['organisation'] as Record<string, unknown>) ?? {};
    const userOrganisationId =
      (organisation['organisationId'] as string) ||
      (organisation['id'] as string) ||
      '';

    let organisationName = '';
    let organisationType = 'Public';
    let industry = 'forest';
    let department = '';

    if (userOrganisationId && organizationsList.length > 0) {
      const matchedOrg = organizationsList.find(
        (org) =>
          org['id'] === userOrganisationId || org['organisationId'] === userOrganisationId,
      );
      if (matchedOrg) {
        organisationName =
          (matchedOrg['orgName'] as string) || (matchedOrg['name'] as string) || '';
        organisationType =
          (matchedOrg['organisationType'] as string) ||
          (matchedOrg['type'] as string) ||
          'Public';
        industry = (matchedOrg['industry'] as string) || 'forest';
        department =
          (matchedOrg['department'] as string) ||
          (matchedOrg['dept'] as string) ||
          (matchedOrg['departmentName'] as string) ||
          '';
      }
    }

    const uploadedBy =
      (result['email'] as string) ||
      (result['emailAddress'] as string) ||
      (result['registeredEmail'] as string) ||
      (result['userName'] as string) ||
      (result['name'] as string) ||
      '';

    const accessPolicy = this.mapAccessPolicy(basic.permission);
    const license = this.resolveLicense(metadata.license, metadata.otherLicense);

    return {
      '@context': 'https://voc.forest-stack.iudx.io/',
      type: ['adex:DataBank'],
      name: basic.title?.trim() ?? '',
      shortDescription: basic.shortDescription?.trim() ?? '',
      description: basic.longDescription?.trim() ?? '',
      tags: metadata.tags ?? [],
      concepts: ['', 'Administration'],
      accessPolicy,
      apdURL: this.catalogueApdUrl,
      organization: organisationName || 'Rajasthan Forestry',
      organizationId: userOrganisationId || '6e5bd047-9eb8-4bba-b72e-f57ce3e23ce6',
      organizationType: organisationType || 'Public',
      industry: industry || 'forest',
      uploadedBy: uploadedBy || 'Rajasthan Forest Department Data Provider',
      verifiedBy: '-',
      ownerUserId:
        (result['userId'] as string) || (result['id'] as string) || '',
      license: license || 'Open Government License, India',
      yearRange: this.formatYearRange(metadata.yearRange) || '2020 - 2021',
      geoCoverage: metadata.geoCoverage?.trim() ?? '',
      uploadFrequency: metadata.uploadFrequency || 'OneTime',
      dataBankType: metadata.datasetType || 'Structured',
      fileFormat: (metadata.fileFormat || 'csv').toLowerCase(),
      ...(metadata.dataSample != null ? { dataSample: metadata.dataSample } : {}),
      resourceServer: this.buildResourceServerArray(resourceServer, ogcOnboardingMode),
      dataDescriptor: buildDataDescriptorPayload(metadata.dataDescriptors ?? []),
    };
  }

  private formatYearRange(yearRange: NgsildYearRange | null): string {
    if (!yearRange?.startYear || !yearRange?.endYear) {
      return '';
    }
    return `${yearRange.startYear} - ${yearRange.endYear}`;
  }

  private buildResourceServerArray(
    resourceServer: ResourceServerId,
    ogcOnboardingMode?: OgcOnboardingMode | null,
  ): Array<Record<string, unknown>> {
    return buildResourceServerPayloadArray(
      resourceServer,
      this.resourceServerUrls,
      ogcOnboardingMode,
    );
  }
}
